import React, { useEffect, useMemo, useState } from "react";
import { FiInfo, FiSpeaker } from "react-icons/fi";
import { useAuth } from "../../contexts/AuthContext";
import { getAppVersions, updateLastSeenVersion } from "../../lib/supabase/appVersions";
import VersionUpdatesModal from "./VersionUpdatesModal";

const AboutNotifyButton = ({ isSidebarOpen = true }) => {
    const { userProfile, company, refreshProfile } = useAuth();

    const [open, setOpen] = useState(false);
    const [versions, setVersions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [hasUnreadVersion, setHasUnreadVersion] = useState(false);

    const currentVersion = company?.app_version || null;
    const lastSeenVersion = userProfile?.last_seen_version || null;

    const latestVersion = versions[0] || null;
    const previousVersions = versions.slice(1, 6);

    useEffect(() => {
        if (!company?.id) return;

        const load = async () => {
            setLoading(true);
            const { data } = await getAppVersions(company.id, 6);
            setVersions(data || []);
            setLoading(false);
        };

        load();
    }, [company?.id]);

    useEffect(() => {
        if (!currentVersion) return;
        setHasUnreadVersion(lastSeenVersion !== currentVersion);
    }, [currentVersion, lastSeenVersion]);

    useEffect(() => {
        if (!currentVersion || !userProfile?.id) return;
        if (lastSeenVersion === currentVersion) return;

        // auto-open once on load if user has not seen latest version
        setOpen(true);
    }, [currentVersion, lastSeenVersion, userProfile?.id]);

    const buttonLabel = useMemo(() => {
        return hasUnreadVersion ? "What's New" : "About Notify";
    }, [hasUnreadVersion]);

    const handleOpen = () => {
        setOpen(true);
    };

    const handleClose = async () => {
        setOpen(false);

        if (
            currentVersion &&
            userProfile?.id &&
            userProfile?.last_seen_version !== currentVersion
        ) {
            const { error } = await updateLastSeenVersion(userProfile.id, currentVersion);
            if (!error) {
                await refreshProfile();
                setHasUnreadVersion(false);
            }
        }
    };

    return (
        <>
            <button
                type="button"
                onClick={handleOpen}
                className={`flex items-center rounded-xl transition-all group text-zinc-300 hover:bg-zinc-700 ${!isSidebarOpen
                        ? "h-10 w-10 mx-auto justify-center"
                        : "w-full h-11 px-3 gap-3"
                    }`}
                title={!isSidebarOpen ? buttonLabel : ""}
            >
                <div className="relative">
                    {hasUnreadVersion ? (
                        <FiSpeaker className="w-5 h-5 text-white flex-shrink-0" />
                    ) : (
                        <FiInfo className="w-5 h-5 text-white flex-shrink-0" />
                    )}

                    {hasUnreadVersion && (
                        <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-amber-400" />
                    )}
                </div>

                {isSidebarOpen && (
                    <span className="font-medium truncate text-sm">{buttonLabel}</span>
                )}
            </button>

            <VersionUpdatesModal
                isOpen={open}
                onClose={handleClose}
                company={company}
                latestVersion={latestVersion}
                previousVersions={previousVersions}
                loading={loading}
            />
        </>
    );
};

export default AboutNotifyButton;